import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllDebts } from '../services/debtService';
import { Debt, DebtStatus } from '../utils/debtTypes';
import { playConditionalSound } from '../utils/notificationSound';

interface DebtNotificationSummary {
  totalCount: number;
  highPriorityCount: number;
  overdueCount: number;
  dueSoonCount: number;
  highAmountCount: number;
  newDebtCount: number;
  hasNotifications: boolean;
}

interface UseDebtNotificationsReturn {
  summary: DebtNotificationSummary;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  lastUpdated: Date | null;
}

export const useDebtNotifications = (
  refreshInterval: number = 5 * 60 * 1000 // 5 دقائق افتراضياً
): UseDebtNotificationsReturn => {
  const [summary, setSummary] = useState<DebtNotificationSummary>({
    totalCount: 0,
    highPriorityCount: 0,
    overdueCount: 0,
    dueSoonCount: 0,
    highAmountCount: 0,
    newDebtCount: 0,
    hasNotifications: false
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const previousSummaryRef = useRef<DebtNotificationSummary | null>(null);

  const calculateNotifications = useCallback(async (): Promise<DebtNotificationSummary> => {
    try {
      // جلب جميع الديون ثم فلترتها محلياً
      console.log('🔍 جلب جميع الديون لحساب الإشعارات...');
      const allDebtsFromDB = await getAllDebts(undefined, 200);
      console.log(`📊 تم جلب ${allDebtsFromDB.length} دين من قاعدة البيانات`);

      // فلترة الديون النشطة والمسددة جزئياً
      const allDebts = allDebtsFromDB.filter(debt =>
        debt.status === DebtStatus.ACTIVE || debt.status === DebtStatus.PARTIALLY_PAID
      );
      console.log(`✅ الديون النشطة والمسددة جزئياً: ${allDebts.length}`);
      
      const now = new Date();
      const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      let overdueCount = 0;
      let dueSoonCount = 0;
      let highAmountCount = 0;
      let newDebtCount = 0;
      let highPriorityCount = 0;

      allDebts.forEach(debt => {
        // الديون المتأخرة
        if (debt.dueDate && new Date(debt.dueDate) < now) {
          overdueCount++;
          const daysOverdue = Math.floor((now.getTime() - new Date(debt.dueDate).getTime()) / (1000 * 60 * 60 * 24));
          if (daysOverdue > 7) {
            highPriorityCount++;
          }
        }

        // الديون القريبة من الاستحقاق
        if (debt.dueDate && new Date(debt.dueDate) > now && new Date(debt.dueDate) <= threeDaysFromNow) {
          dueSoonCount++;
        }

        // الديون عالية القيمة
        if (debt.remainingAmount >= 5000) {
          highAmountCount++;
          if (debt.remainingAmount >= 10000) {
            highPriorityCount++;
          }
        }

        // الديون الجديدة
        if (debt.createdAt && new Date(debt.createdAt) > sevenDaysAgo) {
          newDebtCount++;
        }
      });

      const totalCount = overdueCount + dueSoonCount + highAmountCount + newDebtCount;

      return {
        totalCount,
        highPriorityCount,
        overdueCount,
        dueSoonCount,
        highAmountCount,
        newDebtCount,
        hasNotifications: totalCount > 0
      };
    } catch (err) {
      console.error('Error calculating debt notifications:', err);
      throw new Error('فشل في حساب إشعارات الديون');
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const newSummary = await calculateNotifications();

      // تشغيل صوت تنبيه إذا كانت هناك ديون متأخرة جديدة
      if (previousSummaryRef.current) {
        const previousOverdue = previousSummaryRef.current.overdueCount;
        const newOverdue = newSummary.overdueCount;

        if (newOverdue > previousOverdue) {
          // ديون متأخرة جديدة
          playConditionalSound('debt', 'error');
        } else if (newSummary.dueSoonCount > previousSummaryRef.current.dueSoonCount) {
          // ديون قريبة من الاستحقاق جديدة
          playConditionalSound('debt', 'warning');
        }
      }

      previousSummaryRef.current = newSummary;
      setSummary(newSummary);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error refreshing debt notifications:', err);
      setError(err instanceof Error ? err.message : 'حدث خطأ غير متوقع');
    } finally {
      setLoading(false);
    }
  }, [calculateNotifications]);

  // التحديث التلقائي
  useEffect(() => {
    // التحديث الأولي
    refresh();

    // إعداد التحديث الدوري
    const interval = setInterval(refresh, refreshInterval);

    // تنظيف المؤقت عند إلغاء المكون
    return () => clearInterval(interval);
  }, [refresh, refreshInterval]);

  // التحديث عند التركيز على النافذة
  useEffect(() => {
    const handleFocus = () => {
      // تحديث فقط إذا مر أكثر من دقيقة على آخر تحديث
      if (lastUpdated && Date.now() - lastUpdated.getTime() > 60000) {
        refresh();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [lastUpdated, refresh]);

  return {
    summary,
    loading,
    error,
    refresh,
    lastUpdated
  };
};

export default useDebtNotifications;
